import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  createBlogPost,
  getBlogPosts,
  getBlogPostById,
  updateBlogPost,
  deleteBlogPost,
} from '@app/api/blog.api';
import { BlogPost, CreateBlogPostDto, UpdateBlogPostDto, BlogFilters, BlogPaginatedResponse } from '@app/types/api.types';

export interface BlogState {
  items: BlogPost[];
  currentItem: BlogPost | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  filters: BlogFilters;
  loading: boolean;
  error: string | null;
}

const initialState: BlogState = {
  items: [],
  currentItem: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  },
  filters: {
    page: 1,
    limit: 10,
  },
  loading: false,
  error: null,
};

export const fetchBlogPosts = createAsyncThunk(
  'blog/fetchBlogPosts',
  async (filters: BlogFilters = { page: 1, limit: 10 }, { rejectWithValue }) => {
    try {
      const response = await getBlogPosts(filters);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch blog posts');
    }
  }
);

export const fetchBlogPostById = createAsyncThunk(
  'blog/fetchBlogPostById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await getBlogPostById(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch blog post');
    }
  }
);

export const createBlog = createAsyncThunk(
  'blog/createBlog',
  async (data: CreateBlogPostDto, { rejectWithValue }) => {
    try {
      const response = await createBlogPost(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create blog post');
    }
  }
);

export const updateBlog = createAsyncThunk(
  'blog/updateBlog',
  async ({ id, data }: { id: string; data: UpdateBlogPostDto }, { rejectWithValue }) => {
    try {
      const response = await updateBlogPost(id, data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update blog post');
    }
  }
);

export const deleteBlog = createAsyncThunk(
  'blog/deleteBlog',
  async (id: string, { rejectWithValue }) => {
    try {
      await deleteBlogPost(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to delete blog post');
    }
  }
);

const blogSlice = createSlice({
  name: 'blog',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<BlogFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearCurrentItem: (state) => {
      state.currentItem = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Blog Posts
    builder.addCase(fetchBlogPosts.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchBlogPosts.fulfilled, (state, action) => {
      state.loading = false;
      state.items = action.payload.data;
      state.pagination = {
        page: action.payload.page,
        limit: action.payload.limit,
        total: action.payload.total,
        totalPages: action.payload.totalPages || Math.ceil(action.payload.total / action.payload.limit),
        hasNext: action.payload.hasNext || action.payload.page < (action.payload.totalPages || Math.ceil(action.payload.total / action.payload.limit)),
        hasPrev: action.payload.hasPrev || action.payload.page > 1,
      };
    });
    builder.addCase(fetchBlogPosts.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Fetch Blog Post By ID
    builder.addCase(fetchBlogPostById.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchBlogPostById.fulfilled, (state, action) => {
      state.loading = false;
      state.currentItem = action.payload;
    });
    builder.addCase(fetchBlogPostById.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Create Blog Post
    builder.addCase(createBlog.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(createBlog.fulfilled, (state, action) => {
      state.loading = false;
      state.items.unshift(action.payload);
    });
    builder.addCase(createBlog.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Update Blog Post
    builder.addCase(updateBlog.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(updateBlog.fulfilled, (state, action) => {
      state.loading = false;
      const index = state.items.findIndex((item) => item.id === action.payload.id);
      if (index !== -1) {
        state.items[index] = action.payload;
      }
      if (state.currentItem?.id === action.payload.id) {
        state.currentItem = action.payload;
      }
    });
    builder.addCase(updateBlog.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Delete Blog Post
    builder.addCase(deleteBlog.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(deleteBlog.fulfilled, (state, action) => {
      state.loading = false;
      state.items = state.items.filter((item) => item.id !== action.payload);
      if (state.currentItem?.id === action.payload) {
        state.currentItem = null;
      }
    });
    builder.addCase(deleteBlog.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
  },
});

export const { setFilters, clearCurrentItem } = blogSlice.actions;
export default blogSlice.reducer;

